import { useMemo } from "react";

import { selectCurrentAccount, selectCurrentSubscription } from "fond/api";
import { BillingCycle, Maybe } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";

export interface BillingCycles {
  /**
   * Billing cycle with status "ACTIVE" indicating the currently active billing cycle.
   */
  active: Maybe<BillingCycle>;
  /**
   * Billing cycle with status "NOT_STARTED" indicating a future scheduled billing cycle.
   */
  future: Maybe<BillingCycle>;
  /**
   * Billing cycle with status "PENDING_ACTIVATION" indicating a billing cycle pending payment (e.g. an in cycle upgrade).
   */
  pending: Maybe<BillingCycle>;
  /**
   * Billing cycle with status "EXPIRED" indicating the most recently expired billing cycle.
   */
  expired: Maybe<BillingCycle>;
}

/**
 * Gets all billing cycle types from the current subscription.
 * Returns a record containing active, future, pending, and expired billing cycles.
 *
 * @returns An object with:
 *  - active: The billing cycle with status "ACTIVE"
 *  - future: The billing cycle with status "NOT_STARTED"
 *  - pending: The billing cycle with status "PENDING_ACTIVATION"
 *  - expired: The billing cycle with status "EXPIRED"
 */
export const useGetBillingCycles = (): BillingCycles => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentSubscription = useAppSelector((state) => currentAccount && selectCurrentSubscription(state, currentAccount.ID));

  return useMemo(() => {
    return {
      active: currentSubscription?.BillingCycles.find(({ Status }) => Status === "ACTIVE"),
      future: currentSubscription?.BillingCycles.find(({ Status }) => Status === "NOT_STARTED"),
      pending: currentSubscription?.BillingCycles.find(({ Status }) => Status === "PENDING_ACTIVATION"),
      expired: currentSubscription?.BillingCycles.find(({ Status }) => Status === "EXPIRED"),
    };
  }, [currentSubscription]);
};
